import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PenTool } from 'lucide-react';
import { useApiClient } from '../../lib/api/api-context.js';
import { getReadUrl } from '../../lib/media/media-api.js';
import { cn, Dialog, DialogContent, DialogTitle, Skeleton } from '@tailoring-crm/ui';

/**
 * A measurement sketch as a PICTURE — what the counter, the order page and the karigar see.
 *
 * Never the canvas: the drawing tool is a megabyte, and somebody reading measurements off a phone
 * at a cutting table needs to see the drawing, not edit it. So this shows the PNG the canvas
 * exported when it was saved, the thumbnail inline and the full image when it is tapped — the
 * read-only view. Opening the drawing to change it is a separate, explicit action elsewhere.
 *
 * Read URLs are signed and short-lived, so they are fetched per view rather than stored, exactly
 * as `ProductImage` and the media lightbox do.
 */
export interface SketchPictureProps {
  /** The PNG's media id. Null when the drawing saved but its picture could not be made. */
  previewMediaId: string | null;
  /** Whose sketch, for the alt text: "Priya — blouse". */
  name: string;
  className?: string;
}

export function SketchPicture({
  previewMediaId,
  name,
  className,
}: SketchPictureProps): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const [open, setOpen] = useState(false);

  const thumb = useQuery({
    queryKey: ['media', previewMediaId, 'url', 'thumb'],
    queryFn: () => getReadUrl(client, previewMediaId as string, 'thumb'),
    enabled: previewMediaId !== null,
    gcTime: 0,
    staleTime: 60_000,
    retry: 1,
  });
  const full = useQuery({
    queryKey: ['media', previewMediaId, 'url', 'full'],
    queryFn: () => getReadUrl(client, previewMediaId as string, 'full'),
    enabled: open && previewMediaId !== null,
    gcTime: 0,
    staleTime: 60_000,
    retry: 1,
  });

  const frame = cn(
    'flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-white',
    className,
  );

  // A drawing with no picture still exists — it is just not viewable until it is next saved.
  if (!previewMediaId) {
    return (
      <span className={cn(frame, 'flex-col gap-1 bg-muted p-2 text-center text-muted-foreground')}>
        <PenTool aria-hidden="true" className="size-5" />
        <span className="text-xs">{t('measurementSketch.noPicture')}</span>
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        className={cn(frame, 'cursor-zoom-in focus-visible:ring-2 focus-visible:ring-ring')}
        aria-label={t('measurementSketch.open', { name })}
        onClick={() => setOpen(true)}
      >
        {thumb.data ? (
          <img
            src={thumb.data.url}
            alt={t('measurementSketch.alt', { name })}
            loading="lazy"
            className="size-full object-contain"
          />
        ) : (
          <Skeleton className="size-full" />
        )}
      </button>

      {open ? (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-3xl">
            <DialogTitle className="pr-8 text-base">
              {t('measurementSketch.title', { name })}
            </DialogTitle>
            <div className="flex min-h-[50vh] items-center justify-center rounded-md bg-white">
              {full.isPending ? (
                <Skeleton className="h-[50vh] w-full" />
              ) : full.isError || !full.data ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  {t('media.lightbox.failed')}
                </p>
              ) : (
                <img
                  src={full.data.url}
                  alt={t('measurementSketch.alt', { name })}
                  className="max-h-[75vh] w-auto max-w-full object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
